import { useState } from "react";
import { Download } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { base44 } from "@/api/base44Client";
import { shouldTrack } from "@/lib/pageTracking";

const LOGO = "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png";

export default function ToolExportButton({ slug, title, targetRef, fileName }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!targetRef?.current) return;
    setExporting(true);
    await new Promise(r => setTimeout(r, 200));
    try {
      const canvas = await html2canvas(targetRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, windowWidth: 1100 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const buttonArea = 24;
      const availW = pdfW - 2 * margin;
      const availH = pdfH - 2 * margin - buttonArea;
      const imgRatio = canvas.width / canvas.height;
      const pageRatio = availW / availH;
      let imgW, imgH;
      if (imgRatio > pageRatio) { imgW = availW; imgH = availW / imgRatio; }
      else { imgH = availH; imgW = availH * imgRatio; }
      const offsetX = (pdfW - imgW) / 2;
      const offsetY = margin;
      pdf.addImage(imgData, "PNG", offsetX, offsetY, imgW, imgH);
      // "Run a new report" button at the bottom — clickable link back to the tool
      const btnW = 70, btnH = 12;
      const btnX = (pdfW - btnW) / 2;
      const btnY = pdfH - margin - btnH;
      pdf.setFillColor(255, 32, 79);
      pdf.roundedRect(btnX, btnY, btnW, btnH, 3, 3, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text("Run a new report", pdfW / 2, btnY + btnH / 2 + 1.5, { align: "center" });
      pdf.link(btnX, btnY, btnW, btnH, { url: `https://www.truestays.co.uk/resources/tools/${slug}` });
      pdf.save(`${fileName || title}.pdf`);
      // Track export for admin analytics
      shouldTrack().then((track) => {
        if (!track) return;
        base44.entities.ResourceEvent.create({ page: `tool:${slug}`, event_type: "click", element_label: "pdf_export" }).catch(() => {});
      });
    } catch (err) { console.error(err); }
    setExporting(false);
  };

  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
      <button onClick={handleExport} disabled={exporting} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "11px 20px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: exporting ? "wait" : "pointer", opacity: exporting ? 0.7 : 1 }}>
        <Download size={16} /> {exporting ? "Exporting…" : "Export PDF"}
      </button>
    </div>
  );
}